import type { Team } from "@redotech/redo-model/team";
import { safePredicate } from "./safe-predicate";
import type {
  AsyncTaskStatuses,
  IntakeState,
  OverallProgress,
  ProductSetupConfig,
  SetupProgress,
  SetupTask,
} from "./types";

/**
 * Effective completion: derived predicate OR manual override.
 * Manual override is for tasks without a derivable predicate (e.g.
 * "watch the demo video"). Predicates are wrapped in safePredicate so a
 * throw doesn't propagate into React render.
 */
export function isTaskComplete(
  task: SetupTask,
  team: Team,
  progress: SetupProgress,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): boolean {
  const asyncStatus = asyncTaskStatuses[task.id];
  if (asyncStatus) {
    return asyncStatus.complete;
  }
  const derived = safePredicate(
    progress.product,
    task.id,
    task.completionPredicate,
  )(team, progress);
  if (derived) {
    return true;
  }
  return progress.taskStates[task.id]?.manuallyCompletedAt != null;
}

export function getTaskDescription(
  task: SetupTask,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): string {
  return asyncTaskStatuses[task.id]?.description ?? task.description;
}

export function isTaskVisible(task: SetupTask, team: Team): boolean {
  return task.visiblePredicate?.(team) ?? true;
}

/**
 * Aggregate progress for the widget header + header tab.
 *
 * Counts ONLY tasks that are either (a) requiredForLive OR (b) NOT
 * requiredForLive but the merchant has visited at least once. Avoids
 * counting unsurfaced optional tasks against the percentage so the bar
 * can hit 100% when functionally done. (#17)
 */
export function computeOverallProgress(
  config: ProductSetupConfig,
  team: Team,
  progress: SetupProgress,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): OverallProgress {
  const tasks = Object.values(config.tasks).filter((task) =>
    isTaskVisible(task, team),
  );
  const visible = tasks.filter(
    (t) => t.requiredForLive || progress.taskStates[t.id]?.visitedAt != null,
  );
  const complete = visible.filter((t) =>
    isTaskComplete(t, team, progress, asyncTaskStatuses),
  ).length;
  return { complete, total: visible.length };
}

/** All required-for-live tasks complete = guide considered "live." */
export function isGuideComplete(
  config: ProductSetupConfig,
  team: Team,
  progress: SetupProgress,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): boolean {
  return Object.values(config.tasks)
    .filter((t) => isTaskVisible(t, team))
    .filter((t) => t.requiredForLive)
    .every((t) => isTaskComplete(t, team, progress, asyncTaskStatuses));
}

/**
 * Required-for-live tasks the merchant still needs to do. Drives the
 * dashboard banner ("you won't actually defend disputes until X").
 */
export function getIncompleteRequiredForLive(
  config: ProductSetupConfig,
  team: Team,
  progress: SetupProgress,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): SetupTask[] {
  return Object.values(config.tasks).filter(
    (t) =>
      isTaskVisible(t, team) &&
      t.requiredForLive &&
      !isTaskComplete(t, team, progress, asyncTaskStatuses),
  );
}

/**
 * #16 — extract the slice of team data this product's predicates depend on.
 * Provider memoizes computeOverallProgress on reference equality of this
 * value, so predicates only re-evaluate when relevant team state changes.
 */
export function computeRelevantSlice(
  config: ProductSetupConfig,
  team: Team,
): unknown {
  return config.relevantTeamSlice(team);
}

/** Derived intake state — convenience for components. */
export function deriveIntakeState(progress: SetupProgress): IntakeState {
  return {
    answers: progress.intakeAnswers,
    completed: progress.intakeCompletedAt != null,
    completedByUserId: progress.intakeCompletedByUserId,
  };
}

/**
 * A task is actionable when all its prerequisites are complete.
 * UI uses this to gray out blocked tasks.
 */
export function isTaskActionable(
  task: SetupTask,
  config: ProductSetupConfig,
  team: Team,
  progress: SetupProgress,
  asyncTaskStatuses: AsyncTaskStatuses = {},
): boolean {
  if (!task.prerequisites?.length) {
    return true;
  }
  return task.prerequisites.every((prereqId) => {
    const prereq = config.tasks[prereqId];
    if (!prereq) {
      return true;
    } // unknown prereq = treat as satisfied (defensive)
    if (!isTaskVisible(prereq, team)) {
      return true;
    }
    return isTaskComplete(prereq, team, progress, asyncTaskStatuses);
  });
}

export function getTasksCompletedByRoute(
  config: ProductSetupConfig,
  pathname: string,
): SetupTask[] {
  return Object.values(config.tasks).filter((task) =>
    task.completeOnRoute?.test(pathname),
  );
}
